import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Avatar, Input, Button, App as AntdApp, Spin, Tag, Tabs, Empty } from 'antd';
import {
  Send, MessageCircle, FilePlus, Check, X, KeyRound,
  FileEdit, Trash2, History as HistoryIcon, MessageSquare,
} from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/vi';
import { signingService, type SigningEvent } from '@/services/signingService';

dayjs.extend(relativeTime);
dayjs.locale('vi');

/**
 * V1.0.40: Khối Bình luận + Lịch sử cho 1 yêu cầu ký.
 * 2 tab:
 *  - "Bình luận": chỉ event COMMENT (text trao đổi)
 *  - "Lịch sử": tất cả events (CREATE/SIGN/REJECT/COMMENT...) — timeline
 */
export function SigningCommentsHistory({ requestId, canComment }: {
  requestId: string;
  /** Hiển thị form thêm bình luận hay không. Mặc định true. */
  canComment?: boolean;
}) {
  const { message } = AntdApp.useApp();
  const qc = useQueryClient();
  const [tab, setTab] = useState<'comments' | 'history'>('comments');
  const [draft, setDraft] = useState('');

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['signing-events', requestId],
    queryFn: () => signingService.getEvents(requestId),
    refetchInterval: 30_000,
  });

  const addMut = useMutation({
    mutationFn: (content: string) => signingService.addComment(requestId, content),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: ['signing-events', requestId] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? 'Gửi bình luận thất bại'),
  });

  const comments = events.filter(e => e.action === 'COMMENT');
  const allowComment = canComment !== false;

  const handleSend = () => {
    const txt = draft.trim();
    if (!txt) return;
    if (txt.length > 2000) {
      message.warning('Bình luận tối đa 2000 ký tự.');
      return;
    }
    addMut.mutate(txt);
  };

  return (
    <Card
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <MessageSquare size={15} />
          Bình luận & Lịch sử
        </span>
      }
      bodyStyle={{ padding: 0 }}
      size="small"
    >
      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as any)}
        items={[
          {
            key: 'comments',
            label: (
              <span>
                <MessageCircle size={13} style={{ verticalAlign: -2, marginRight: 4 }} />
                Bình luận
                {comments.length > 0 && (
                  <Tag style={{ marginLeft: 6, fontSize: 10 }}>{comments.length}</Tag>
                )}
              </span>
            ),
          },
          {
            key: 'history',
            label: (
              <span>
                <HistoryIcon size={13} style={{ verticalAlign: -2, marginRight: 4 }} />
                Lịch sử
                {events.length > 0 && (
                  <Tag style={{ marginLeft: 6, fontSize: 10 }}>{events.length}</Tag>
                )}
              </span>
            ),
          },
        ]}
        tabBarStyle={{ paddingLeft: 16, paddingRight: 16, marginBottom: 0 }}
      />

      <div style={{ padding: 16, paddingTop: 12, minHeight: 200 }}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 30 }}><Spin /></div>
        ) : tab === 'comments' ? (
          <CommentsTab events={comments} />
        ) : (
          <HistoryTab events={events} />
        )}
      </div>

      {tab === 'comments' && allowComment && (
        <div style={{ padding: 16, borderTop: '1px solid #f0f0f0', background: '#fafbfc' }}>
          <Input.TextArea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Viết bình luận của bạn (Shift+Enter để xuống dòng)..."
            rows={2}
            maxLength={2000}
            showCount
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <span style={{ fontSize: 11, color: '#7d8a98' }}>
              💡 Bình luận chỉ hiển thị cho người tạo yêu cầu và các người ký.
            </span>
            <Button
              type="primary"
              size="small"
              icon={<Send size={14} />}
              onClick={handleSend}
              loading={addMut.isPending}
              disabled={!draft.trim()}
            >
              Gửi
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ===================================
// Tab Bình luận
// ===================================
function CommentsTab({ events }: { events: SigningEvent[] }) {
  if (events.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 30, color: '#7d8a98' }}>
        <MessageCircle size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
        <div style={{ fontSize: 13 }}>Chưa có bình luận nào.</div>
        <div style={{ fontSize: 11, marginTop: 4 }}>
          Trao đổi với người tạo yêu cầu và đồng nghiệp ký trước khi xác nhận ký.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {events.map(e => (
        <div key={e.id} style={{ display: 'flex', gap: 10 }}>
          <Avatar
            size={32}
            src={e.actorAvatar ?? undefined}
            style={{ flexShrink: 0, background: e.actorAvatar ? undefined : '#1e6feb' }}
          >
            {(e.actorName ?? '?').charAt(0).toUpperCase()}
          </Avatar>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>{e.actorName ?? 'Hệ thống'}</span>
              <span style={{ fontSize: 11, color: '#7d8a98' }}>
                {dayjs(e.createdAt).fromNow()} · {dayjs(e.createdAt).format('HH:mm DD/MM/YYYY')}
              </span>
            </div>
            <div style={{
              fontSize: 13, lineHeight: 1.5,
              background: '#f8faff', padding: '8px 12px',
              borderRadius: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {e.content}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ===================================
// Tab Lịch sử (timeline)
// ===================================

const ACTION_CONFIG: Record<string, { label: string; color: string; icon: React.ComponentType<any> }> = {
  CREATE_REQUEST: { label: 'Tạo yêu cầu ký', color: '#1e6feb', icon: FilePlus },
  ADD_SIGNER:     { label: 'Thêm người ký', color: '#722ed1', icon: FilePlus },
  OTP_REQUEST:    { label: 'Yêu cầu OTP',    color: '#faad14', icon: KeyRound },
  SIGN:           { label: 'Đã ký',          color: '#52c41a', icon: Check },
  REJECT:         { label: 'Từ chối ký',     color: '#f5222d', icon: X },
  COMMENT:        { label: 'Bình luận',      color: '#7d8a98', icon: MessageCircle },
  DELETE:         { label: 'Đã xóa yêu cầu', color: '#f5222d', icon: Trash2 },
  UPDATE_POSITION:{ label: 'Cập nhật vị trí ký', color: '#faad14', icon: FileEdit },
};

function HistoryTab({ events }: { events: SigningEvent[] }) {
  if (events.length === 0) {
    return <Empty description="Chưa có lịch sử." />;
  }

  return (
    <div style={{ position: 'relative', paddingLeft: 30 }}>
      {/* Line dọc bên trái */}
      <div style={{
        position: 'absolute', left: 14, top: 8, bottom: 8,
        width: 2, background: '#e8edf2',
      }} />

      {events.map((e, idx) => {
        const cfg = ACTION_CONFIG[e.action] ?? { label: e.action, color: '#7d8a98', icon: HistoryIcon };
        const Icon = cfg.icon;
        const isLast = idx === events.length - 1;

        return (
          <div key={e.id} style={{ position: 'relative', paddingBottom: isLast ? 0 : 16 }}>
            {/* Icon tròn */}
            <div style={{
              position: 'absolute', left: -30, top: 0,
              width: 28, height: 28, borderRadius: '50%',
              background: '#fff', border: `2px solid ${cfg.color}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: cfg.color, zIndex: 1,
            }}>
              <Icon size={13} />
            </div>

            {/* Nội dung */}
            <div style={{ paddingLeft: 6 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <Tag color={cfg.color} style={{ marginInlineEnd: 0, fontWeight: 600 }}>
                  {cfg.label}
                </Tag>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{e.actorName ?? 'Hệ thống'}</span>
                {e.targetUserName && (
                  <span style={{ fontSize: 12, color: '#5b6875' }}>
                    → <strong>{e.targetUserName}</strong>
                  </span>
                )}
                <span style={{ fontSize: 11, color: '#7d8a98' }}>
                  {dayjs(e.createdAt).format('HH:mm DD/MM/YYYY')}
                </span>
              </div>
              {e.content && (
                <div style={{
                  marginTop: 6, fontSize: 12, color: '#48535e',
                  background: e.action === 'REJECT' ? '#fff1f0' : '#f8faff',
                  padding: '6px 10px', borderRadius: 6,
                  borderLeft: `3px solid ${cfg.color}`,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {e.action === 'REJECT' && <strong style={{ color: '#f5222d' }}>Lý do: </strong>}
                  {e.content}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default SigningCommentsHistory;
